import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

/**
 * @summary This component is meant to be used inside a dialog element, to display a disruptive message with an icon and a message.
 * @property iconName - The name of icon displayed in the component. This value needs to match the name of the icon web component, i.e. 'ds-icon-notification_warning_XL', 'ds-icon-notification_info_XL' etc.
 * For proper icon display in relation to the text content, please use the _XL variant of the icon. These icon variants are showcased here https://rcd-static.npunix.bitdefender.biz/storybooks/nirvana/latest/?path=/story/design-system-all-icons--code-base-icon-pack
 * @property iconColor - The color of the icon. It can be a HEX code or a CSS color variable.
 * @slot message - The slot where the message content is placed. In order to correctly display the component, the message should be a <gz-text> component.
 * @tag gz-dialog-notification-content
 */
@customElement('gz-dialog-notification-content')
export class GzDialogNotificationContentWebComponent extends LitElement {
  static get styles() {
    return css`
      :host {
        display: block;
      }
      .icon {
        float: left;
        padding-right: var(--spacer);
        padding-bottom: var(--spacer);
      }
    `;
  }

  /**
   * @type {string} iconName - this value needs to match the name of the icon web component, i.e. 'ds-icon-notification_warning', 'ds-icon-notification_negative' etc.
   * The component provides support for general use icons, found in the default icon map: http://rcd-static.npunix.bitdefender.biz/storybooks/nirvana/latest/?path=/story/design-system--icons
   */
  @property({ type: String, reflect: true })
  iconName: string;

  /**
   * @type {string} iconColor - this value provides the color of the icon. It can be a HEX code or a CSS color variable.
   */
  @property({ type: String, reflect: true })
  iconColor: string;

  /**
   * @type {number} textOffsetTop - this value provides the padding-top of the text element.
   * It is used to adjust the position of the text element in relation to the icon.
   */
  @property({ type: String, reflect: true })
  textOffsetTop = 0;

  render() {
    const styles = {
      paddingTop: this.textOffsetTop ? `${this.textOffsetTop}px` : '0',
    };
    return html`
      <div class="container">
        <span class="icon">
          <gz-icon .name="${this.iconName}" .color="${this.iconColor}" size="56"></gz-icon>
        </span>
        <gz-text fontSize="14" style=${styleMap(styles)}>
          <slot name="message"></slot>
        </gz-text>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-dialog-notification-content': GzDialogNotificationContentWebComponent;
  }
}
